'use client';

import { useCallback, useEffect, useState } from 'react';

import { $isCodeNode } from '@lexical/code-core';
import { $isListNode, ListNode } from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isHeadingNode, $isQuoteNode } from '@lexical/rich-text';
import { $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import classnames from 'classnames';
import {
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_LOW,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  INDENT_CONTENT_COMMAND,
  OUTDENT_CONTENT_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
} from 'lexical';
import type { ElementFormatType } from 'lexical';

import type { EditorLabels } from '../Editor.types';
import {
  getBlockTypeToBlockName,
  useToolbarState,
} from '../context/ToolbarContext';
import { DropDown } from '../ui/DropDown';
import { DropdownMenuItem } from '../ui/DropdownMenuItem';
import { ToolbarButton } from '../ui/ToolbarButton';
import { $findTopLevelElement } from '../utils/toolbarUtils';
import {
  clearFormatting,
  formatBulletList,
  formatHeading,
  formatNumberedList,
  formatParagraph,
  formatQuote,
  transformToCapitalize,
  transformToLowercase,
  transformToUppercase,
} from './ToolbarPlugin.utils';

import styles from './ToolbarPlugin.module.scss';

function Divider() {
  return <div className={styles['divider']} />;
}

const getElementFormatOptions = (
  labels?: ToolbarLabels,
): {
  [key in Exclude<ElementFormatType, ''>]: {
    icon: string;
    name: string;
  };
} => ({
  center: {
    icon: 'center-align',
    name: labels?.centerAlign ?? 'Center align',
  },
  end: {
    icon: 'right-align',
    name: labels?.endAlign ?? 'End align',
  },
  justify: {
    icon: 'justify-align',
    name: labels?.justifyAlign ?? 'Justify align',
  },
  left: {
    icon: 'left-align',
    name: labels?.leftAlign ?? 'Left align',
  },
  right: {
    icon: 'right-align',
    name: labels?.rightAlign ?? 'Right align',
  },
  start: {
    icon: 'left-align',
    name: labels?.startAlign ?? 'Start align',
  },
});

type ToolbarLabels = EditorLabels | undefined;

function BlockFormatDropDown({
  editor,
  blockType,
  disabled = false,
  labels,
}: {
  blockType: keyof ReturnType<typeof getBlockTypeToBlockName>;
  editor: ReturnType<typeof useLexicalComposerContext>[0];
  disabled?: boolean;
  labels?: ToolbarLabels;
}) {
  const blockTypeToBlockName = getBlockTypeToBlockName(labels);
  const validBlockType =
    blockType && blockType in blockTypeToBlockName ? blockType : 'paragraph';

  return (
    <DropDown
      disabled={disabled}
      buttonClassName={styles['toolbar-item']}
      buttonIconClassName={classnames(styles['format'], styles[validBlockType])}
      buttonLabel={
        blockTypeToBlockName[validBlockType] || blockTypeToBlockName.paragraph
      }
      buttonAriaLabel={
        labels?.formattingOptions ?? 'Formatting options for text style'
      }
    >
      <DropdownMenuItem
        onClick={() => formatParagraph(editor)}
        active={blockType === 'paragraph'}
        iconClassName={styles['paragraph']}
        label={blockTypeToBlockName.paragraph}
      />
      <DropdownMenuItem
        onClick={() => formatHeading(editor, blockType, 'h1')}
        active={blockType === 'h1'}
        iconClassName={styles['h1']}
        label={blockTypeToBlockName.h1}
      />
      <DropdownMenuItem
        onClick={() => formatHeading(editor, blockType, 'h2')}
        active={blockType === 'h2'}
        iconClassName={styles['h2']}
        label={blockTypeToBlockName.h2}
      />
      <DropdownMenuItem
        onClick={() => formatHeading(editor, blockType, 'h3')}
        active={blockType === 'h3'}
        iconClassName={styles['h3']}
        label={blockTypeToBlockName.h3}
      />
      <DropdownMenuItem
        onClick={() => formatHeading(editor, blockType, 'h4')}
        active={blockType === 'h4'}
        iconClassName={styles['h4']}
        label={blockTypeToBlockName.h4}
      />
      <DropdownMenuItem
        onClick={() => formatHeading(editor, blockType, 'h5')}
        active={blockType === 'h5'}
        iconClassName={styles['h5']}
        label={blockTypeToBlockName.h5}
      />
      <DropdownMenuItem
        onClick={() => formatHeading(editor, blockType, 'h6')}
        active={blockType === 'h6'}
        iconClassName={styles['h6']}
        label={blockTypeToBlockName.h6}
      />
      <DropdownMenuItem
        onClick={() => formatNumberedList(editor, blockType)}
        active={blockType === 'number'}
        iconClassName={styles['numbered-list']}
        label={blockTypeToBlockName.number}
      />
      <DropdownMenuItem
        onClick={() => formatBulletList(editor, blockType)}
        active={blockType === 'bullet'}
        iconClassName={styles['bullet-list']}
        label={blockTypeToBlockName.bullet}
      />
      <DropdownMenuItem
        onClick={() => formatQuote(editor, blockType)}
        active={blockType === 'quote'}
        iconClassName={styles['quote']}
        label={blockTypeToBlockName.quote}
      />
    </DropDown>
  );
}

function ElementFormatDropdown({
  editor,
  value,
  disabled = false,
  labels,
}: {
  editor: ReturnType<typeof useLexicalComposerContext>[0];
  value: ElementFormatType;
  disabled?: boolean;
  labels?: ToolbarLabels;
}) {
  const formatOptions = getElementFormatOptions(labels);
  const formatOption = formatOptions[value || 'left'];

  return (
    <DropDown
      disabled={disabled}
      buttonLabel={formatOption.name}
      buttonIconClassName={classnames(
        styles['format'],
        styles[formatOption.icon],
      )}
      buttonClassName={styles['toolbar-item']}
      buttonAriaLabel={
        labels?.textAlignment ?? 'Formatting options for text alignment'
      }
    >
      <DropdownMenuItem
        onClick={() => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
        }}
        active={value === 'left'}
        iconClassName={styles['left-align']}
        label={formatOptions.left.name}
      />
      <DropdownMenuItem
        onClick={() => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
        }}
        active={value === 'center'}
        iconClassName={styles['center-align']}
        label={formatOptions.center.name}
      />
      <DropdownMenuItem
        onClick={() => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right');
        }}
        active={value === 'right'}
        iconClassName={styles['right-align']}
        label={formatOptions.right.name}
      />
      <DropdownMenuItem
        onClick={() => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify');
        }}
        active={value === 'justify'}
        iconClassName={styles['justify-align']}
        label={formatOptions.justify.name}
      />
      <DropdownMenuItem
        onClick={() => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'start');
        }}
        active={value === 'start'}
        iconClassName={styles['left-align']}
        label={formatOptions.start.name}
      />
      <DropdownMenuItem
        onClick={() => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'end');
        }}
        active={value === 'end'}
        iconClassName={styles['right-align']}
        label={formatOptions.end.name}
      />
    </DropDown>
  );
}

function AdditionalTextFormatDropdown({
  editor,
  isLowercase,
  isUppercase,
  isSubscript,
  isSuperscript,
  isCapitalize,
  disabled = false,
  labels,
}: {
  editor: ReturnType<typeof useLexicalComposerContext>[0];
  isLowercase: boolean;
  isUppercase: boolean;
  isSubscript: boolean;
  isSuperscript: boolean;
  isCapitalize: boolean;
  disabled?: boolean;
  labels?: ToolbarLabels;
}) {
  return (
    <DropDown
      disabled={disabled}
      buttonClassName={styles['toolbar-item']}
      buttonLabel=""
      buttonAriaLabel={
        labels?.additionalTextStyles ??
        'Formatting options for additional text styles'
      }
      buttonIconClassName={classnames(
        styles['format'],
        styles['dropdown-more'],
      )}
    >
      <DropdownMenuItem
        onClick={() => {
          transformToLowercase(editor);
        }}
        active={isLowercase}
        iconClassName={styles['lowercase']}
        label={labels?.lowercase ?? 'Lowercase'}
      />
      <DropdownMenuItem
        onClick={() => {
          transformToUppercase(editor);
        }}
        active={isUppercase}
        iconClassName={styles['uppercase']}
        label={labels?.uppercase ?? 'Uppercase'}
      />
      <DropdownMenuItem
        onClick={() => {
          transformToCapitalize(editor);
        }}
        active={isCapitalize}
        iconClassName={styles['capitalize']}
        label={labels?.capitalize ?? 'Capitalize'}
      />
      <Divider />
      <DropdownMenuItem
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'subscript');
        }}
        active={isSubscript}
        iconClassName={styles['subscript']}
        label={labels?.subscript ?? 'Subscript'}
      />
      <DropdownMenuItem
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'superscript');
        }}
        active={isSuperscript}
        iconClassName={styles['superscript']}
        label={labels?.superscript ?? 'Superscript'}
      />
    </DropDown>
  );
}

export function ToolbarPlugin({ labels }: { labels?: ToolbarLabels }) {
  const [editor] = useLexicalComposerContext();
  const {
    toolbarState,
    updateToolbarState,
    disabled = false,
  } = useToolbarState();
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [blockType, setBlockType] = useState<string>('paragraph');
  const [elementFormat, setElementFormat] = useState<ElementFormatType>('left');

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      // Update text format
      updateToolbarState('isBold', selection.hasFormat('bold'));
      updateToolbarState('isItalic', selection.hasFormat('italic'));
      updateToolbarState('isUnderline', selection.hasFormat('underline'));
      updateToolbarState(
        'isStrikethrough',
        selection.hasFormat('strikethrough'),
      );
      updateToolbarState('isSubscript', selection.hasFormat('subscript'));
      updateToolbarState('isSuperscript', selection.hasFormat('superscript'));

      // Update block type (works for both collapsed and non-collapsed selections)
      const anchorNode = selection.anchor.getNode();
      const element = $findTopLevelElement(anchorNode);
      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);

      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType<ListNode>(
            anchorNode,
            ListNode,
          );
          const type = parentList
            ? parentList.getListType()
            : element.getListType();
          setBlockType(type);
        } else if ($isHeadingNode(element)) {
          const tag = element.getTag();
          setBlockType(tag);
        } else if ($isQuoteNode(element)) {
          setBlockType('quote');
        } else if ($isCodeNode(element)) {
          setBlockType('code');
        } else {
          const type = $isElementNode(element)
            ? element.getType()
            : 'paragraph';
          setBlockType(type);
        }

        // Update element format (alignment)
        if ($isElementNode(element)) {
          const format = element.getFormatType();
          setElementFormat(format || 'left');
        } else {
          const parent = anchorNode.getParent();
          if ($isElementNode(parent)) {
            const format = parent.getFormatType();
            setElementFormat(format || 'left');
          } else {
            setElementFormat('left');
          }
        }
      }
    }
  }, [editor, updateToolbarState]);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        $updateToolbar();
        return false;
      },
      COMMAND_PRIORITY_CRITICAL,
    );
  }, [editor, $updateToolbar]);

  useEffect(() => {
    editor.getEditorState().read(
      () => {
        $updateToolbar();
      },
      { editor },
    );
  }, [editor, $updateToolbar]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(
          () => {
            $updateToolbar();
          },
          { editor },
        );
      }),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          setCanUndo(payload);
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          setCanRedo(payload);
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor, $updateToolbar]);

  return (
    <div className={styles['toolbar']}>
      <ToolbarButton
        onClick={() => {
          editor.dispatchCommand(UNDO_COMMAND, undefined);
        }}
        disabled={disabled || !canUndo}
        ariaLabel={labels?.undo ?? 'Undo'}
        iconClassName={styles['undo']}
      />
      <ToolbarButton
        onClick={() => {
          editor.dispatchCommand(REDO_COMMAND, undefined);
        }}
        disabled={disabled || !canRedo}
        ariaLabel={labels?.redo ?? 'Redo'}
        iconClassName={styles['redo']}
      />
      <Divider />
      <ToolbarButton
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
        }}
        disabled={disabled}
        active={toolbarState.isBold}
        ariaLabel={labels?.formatBold ?? 'Format Bold'}
        iconClassName={styles['bold']}
      />
      <ToolbarButton
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
        }}
        disabled={disabled}
        active={toolbarState.isItalic}
        ariaLabel={labels?.formatItalic ?? 'Format Italics'}
        iconClassName={styles['italic']}
      />
      <ToolbarButton
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
        }}
        disabled={disabled}
        active={toolbarState.isUnderline}
        ariaLabel={labels?.formatUnderline ?? 'Format Underline'}
        iconClassName={styles['underline']}
      />
      <ToolbarButton
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
        }}
        disabled={disabled}
        active={toolbarState.isStrikethrough}
        ariaLabel={labels?.formatStrikethrough ?? 'Format Strikethrough'}
        iconClassName={styles['strikethrough']}
      />
      <AdditionalTextFormatDropdown
        disabled={disabled}
        editor={editor}
        isLowercase={toolbarState.isLowercase}
        isUppercase={toolbarState.isUppercase}
        isSubscript={toolbarState.isSubscript}
        isSuperscript={toolbarState.isSuperscript}
        isCapitalize={toolbarState.isCapitalize}
        labels={labels}
      />
      <Divider />
      <BlockFormatDropDown
        disabled={disabled}
        blockType={
          blockType as keyof ReturnType<typeof getBlockTypeToBlockName>
        }
        editor={editor}
        labels={labels}
      />
      <Divider />
      <ElementFormatDropdown
        disabled={disabled}
        value={elementFormat}
        editor={editor}
        labels={labels}
      />
      <Divider />
      <ToolbarButton
        onClick={() => {
          editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
        }}
        disabled={disabled}
        ariaLabel={labels?.outdent ?? 'Outdent'}
        iconClassName={styles['outdent']}
      />
      <ToolbarButton
        onClick={() => {
          editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
        }}
        disabled={disabled}
        ariaLabel={labels?.indent ?? 'Indent'}
        iconClassName={styles['indent']}
      />
      <Divider />
      <ToolbarButton
        onClick={() => {
          clearFormatting(editor);
        }}
        disabled={disabled}
        ariaLabel={labels?.clearFormatting ?? 'Clear Formatting'}
        iconClassName={styles['clear-format']}
      />
    </div>
  );
}
